import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import { ReactComponent as ArrowBackIosNewIcon } from './icons/arrow_back_ios_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';
import { ReactComponent as ArrowForwardIosIcon } from './icons/arrow_forward_ios_24dp_5F6368_FILL0_wght400_GRAD0_opsz24.svg';

// ---------------------------------------------------------------------------
// Detect embed mode: dentro un iframe (cross-origin safe) oppure ?embed=1
// ---------------------------------------------------------------------------
const isEmbedMode = (() => {
  try {
    const inIframe = window.self !== window.top;
    const inQuery  = new URLSearchParams(window.location.search).get('embed') === '1';
    return inIframe || inQuery;
  } catch {
    return true; // accesso cross-origin bloccato → siamo in iframe
  }
})();

// ---------------------------------------------------------------------------

function youtubeEmbedUrl(url) {
  const value = String(url || '');
  const match = value.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/i
  );
  return match ? `https://www.youtube.com/embed/${match[1]}` : null;
}

const PANEL_WIDTH = 320;
const TAB_WIDTH = 52;
const SLIDE_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';

const tabFaceSx = {
  width: TAB_WIDTH,
  height: 176,
  flexShrink: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 1,
  bgcolor: 'primary.main',
  color: 'white',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'filter 0.25s ease, background-color 0.25s ease',
  '&:hover': { filter: 'brightness(1.1)' },
};

const ProductMediaSidebar = ({ isOpen, toggleSidebar, productMedia }) => {
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);

  useEffect(() => {
    setCurrentMediaIndex(0);
  }, [productMedia?.urlImage, productMedia?.urlVideo, productMedia?.url3Dviewer]);

  // Costruisci l'array dei media disponibili
  const mediaItems = [];
  if (productMedia) {
    if (productMedia.urlImage)   mediaItems.push({ type: 'image', content: productMedia.urlImage });
    if (productMedia.urlVideo)   mediaItems.push({ type: 'video', content: productMedia.urlVideo });
    if (productMedia.url3Dviewer) {
      // Doppia garanzia: il backend già converte, ma sanitizziamo anche qui
      const secureUrl = productMedia.url3Dviewer.replace(/^http:\/\//i, 'https://');
      mediaItems.push({ type: '3d', content: secureUrl });
    }
  }

  const handlePrev = () =>
    setCurrentMediaIndex((i) => (i === 0 ? mediaItems.length - 1 : i - 1));
  const handleNext = () =>
    setCurrentMediaIndex((i) => (i === mediaItems.length - 1 ? 0 : i + 1));

  const tabLabel = (
    <>
      <PermMediaIcon sx={{ fontSize: '1.4rem' }} />
      <Typography
        sx={{
          fontSize: '0.72rem',
          fontWeight: 700,
          letterSpacing: '0.14em',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: 'rotate(180deg)',
          lineHeight: 1,
        }}
      >
        MEDIA
      </Typography>
    </>
  );

  // ── MODALITÀ NORMALE (browser standalone) ────────────────────────────────
  if (!isEmbedMode) {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          right: '100%',
          display: 'flex',
          alignItems: 'center',
          zIndex: 100,
          transform: isOpen
            ? 'translate3d(0, -50%, 0)'
            : `translate3d(calc(100% - ${TAB_WIDTH}px), -50%, 0)`,
          transition: `transform 0.55s ${SLIDE_EASE}`,
          willChange: 'transform',
        }}
      >
        <Box
          onClick={toggleSidebar}
          role="button"
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Chiudi media' : 'Apri media'}
          sx={{
            ...tabFaceSx,
            borderRadius: '24px 0 0 24px',
            boxShadow: '-6px 4px 18px rgba(0,0,0,0.16)',
          }}
        >
          {tabLabel}
        </Box>

        <Box
          sx={{
            width: PANEL_WIDTH,
            minHeight: 240,
            maxHeight: '80vh',
            boxSizing: 'border-box',
            bgcolor: 'grey.100',
            p: 2.5,
            overflowY: 'auto',
            boxShadow: 3,
            pointerEvents: isOpen ? 'auto' : 'none',
            borderRadius: '16px 0 0 16px',
          }}
        >
          <SidebarContent
            mediaItems={mediaItems}
            currentMediaIndex={currentMediaIndex}
            handlePrev={handlePrev}
            handleNext={handleNext}
          />
        </Box>
      </Box>
    );
  }

  // ── EMBED MODE (iframe) ───────────────────────────────────────────────────
  return (
    <>
      {/* Backdrop semitrasparente – solo quando la sidebar è aperta */}
      <Box
        onClick={toggleSidebar}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.45)',
          backdropFilter: 'blur(2px)',
          zIndex: 9990,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transition: `opacity 0.45s ${SLIDE_EASE}`,
        }}
      />

      {/* Tab laterale – "Media" pill, visibile quando chiusa */}
      <Box
        onClick={toggleSidebar}
        role="button"
        aria-label="Apri media"
        sx={{
          ...tabFaceSx,
          position: 'fixed',
          top: '50%',
          left: 0,
          zIndex: 9999,
          borderRadius: '0 24px 24px 0',
          boxShadow: '6px 4px 18px rgba(0,0,0,0.18)',
          opacity: isOpen ? 0 : 1,
          pointerEvents: isOpen ? 'none' : 'auto',
          transform: isOpen
            ? 'translate3d(-8px, -50%, 0)'
            : 'translate3d(0, -50%, 0)',
          transition: `opacity 0.35s ${SLIDE_EASE}, transform 0.55s ${SLIDE_EASE}, filter 0.25s ease`,
        }}
      >
        {tabLabel}
      </Box>

      {/* Pannello sidebar – slide-in da sinistra sopra la chat */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 9995,
          transform: isOpen ? 'translate3d(0, 0, 0)' : 'translate3d(-100%, 0, 0)',
          transition: `transform 0.55s ${SLIDE_EASE}`,
          willChange: 'transform',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.paper',
          boxShadow: 6,
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      >
        {/* Header del pannello */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: 'primary.main',
            color: 'white',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PermMediaIcon sx={{ fontSize: '1.2rem' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: '0.03em' }}>
              Media del Prodotto
            </Typography>
          </Box>
          <IconButton
            onClick={toggleSidebar}
            size="small"
            sx={{
              color: 'white',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Contenuto scrollabile */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
          <SidebarContent
            mediaItems={mediaItems}
            currentMediaIndex={currentMediaIndex}
            handlePrev={handlePrev}
            handleNext={handleNext}
            embedMode
          />
        </Box>
      </Box>
    </>
  );
};

// ---------------------------------------------------------------------------
// Componente condiviso: contenuto del carosello media
// ---------------------------------------------------------------------------
const SidebarContent = ({ mediaItems, currentMediaIndex, handlePrev, handleNext, embedMode }) => (
  <Paper
    elevation={embedMode ? 0 : 1}
    sx={{
      p: 2,
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      position: 'relative',
      bgcolor: embedMode ? 'transparent' : undefined,
    }}
  >
    {mediaItems.length > 0 ? (
      <>
        {mediaItems.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
            <IconButton size="small" onClick={handlePrev}>
              <ArrowBackIosNewIcon style={{ fontSize: '1rem' }} />
            </IconButton>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {currentMediaIndex + 1} / {mediaItems.length}
            </Typography>
            <IconButton size="small" onClick={handleNext}>
              <ArrowForwardIosIcon style={{ fontSize: '1rem' }} />
            </IconButton>
          </Box>
        )}

        {mediaItems[currentMediaIndex].type === 'image' && (
          <Box sx={{ textAlign: 'center' }}>
            <img
              src={mediaItems[currentMediaIndex].content}
              alt="Media prodotto"
              style={{ width: '100%', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,.15)' }}
            />
          </Box>
        )}

        {mediaItems[currentMediaIndex].type === 'video' && (
          <Box>
            {youtubeEmbedUrl(mediaItems[currentMediaIndex].content) ? (
              <iframe
                src={youtubeEmbedUrl(mediaItems[currentMediaIndex].content)}
                title="Video prodotto"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '100%', height: embedMode ? '40vh' : 180, border: 'none', borderRadius: 8 }}
              />
            ) : (
              <video width="100%" controls style={{ borderRadius: 8 }}>
                <source src={mediaItems[currentMediaIndex].content} type="video/mp4" />
                Il tuo browser non supporta il video.
              </video>
            )}
          </Box>
        )}

        {mediaItems[currentMediaIndex].type === '3d' && (
          <Box>
            <iframe
              src={mediaItems[currentMediaIndex].content}
              title="3D Viewer"
              style={{ width: '100%', height: embedMode ? '55vh' : '300px', border: 'none', borderRadius: 8 }}
            />
          </Box>
        )}
      </>
    ) : (
      <Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary', py: 4 }}>
        Nessun media disponibile per questo prodotto.
      </Typography>
    )}
  </Paper>
);

export default ProductMediaSidebar;
